import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

/**
 * Captures the set of top-level node `localId`s from a ProseMirror document.
 *
 * Used by the apply-anomaly diagnostic events (`target_resolution_failed`,
 * `unexpected_node_removal`) to detect missing or unexpectedly removed
 * nodes after a streaming command runs.
 *
 * Only inspects the top-level children of the document — the design doc
 * defines `unexpected_node_removal` at the top-level node granularity, and
 * scanning the full descendant tree on every apply would be prohibitively
 * expensive for large documents (a Confluence page can contain thousands
 * of nodes).
 *
 * Performance: a single `doc.forEach` over top-level children, no
 * recursion. The returned `Set` provides O(1) membership lookup for the
 * subsequent diff.
 */
export function snapshotTopLevelLocalIds(doc: PMNode | undefined): Set<string> {
	const ids = new Set<string>();
	if (!doc) {
		return ids;
	}
	doc.forEach((child) => {
		const localId = child.attrs?.localId;
		if (typeof localId === 'string' && localId.length > 0) {
			ids.add(localId);
		}
	});
	return ids;
}
